import styled, { css } from 'styled-components';
import { Z_INDEX } from '../../../styles/layout';
import { modeColor } from '../../../styles/themes/utils';
import { setAlpha } from '../../../styles/utils';
import { Box, Stack } from '../../Core';

const MODAL_SHEET_BORDER_RADIUS = 38;
export const MOBILE_MODAL_SHEET_HEADER_HEIGHT = 68;
export const MOBILE_MODAL_SHEET_CONTENT_TOP_PADDING = 20;

function getBottomSheetTop({
  bottomSheetHeight = '50dvh',
  dragOffset,
  isExpanded,
}: {
  bottomSheetHeight?: number | string;
  dragOffset: number;
  isExpanded?: boolean;
}) {
  if (isExpanded) {
    return 'calc(env(safe-area-inset-top) + 22px)';
  }

  const height = typeof bottomSheetHeight === 'number' ? `${bottomSheetHeight}px` : bottomSheetHeight;
  return `max(calc(env(safe-area-inset-top) + 22px), calc(100dvh - ${height} + ${dragOffset}px))`;
}

export const DragHandle = styled(Box)`
  position: absolute;
  top: 8px;
  left: 50%;
  width: 36px;
  transform: translateX(-50%);
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.gray['050']};
  cursor: grab;
  z-index: 10;

  &:active {
    cursor: grabbing;
  }
`;

export const SheetModal = styled(Stack)<{
  isOpen: boolean;
  dragOffset: number;
  bottomSheet?: boolean;
  bottomSheetHeight?: number | string;
  isExpanded?: boolean;
}>`
  position: fixed;
  top: ${({ bottomSheet, bottomSheetHeight, dragOffset, isExpanded }) => {
    if (!bottomSheet || isExpanded) {
      return 'calc(env(safe-area-inset-top) + 22px)';
    }
    return getBottomSheetTop({ bottomSheetHeight, dragOffset, isExpanded });
  }};
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${({ bottomSheet }) => (bottomSheet ? Z_INDEX.modal + 1 : Z_INDEX.modal)};
  background: ${({ theme }) => theme.backgroundContent};
  box-shadow:
    0 0 24px ${({ theme }) => modeColor(theme, theme.colors.black.mute, theme.colors.black.dim)},
    0 0 12px ${({ theme }) => modeColor(theme, theme.colors.black.mute, theme.colors.black.dim)};
  border-radius: ${MODAL_SHEET_BORDER_RADIUS}px ${MODAL_SHEET_BORDER_RADIUS}px 0 0;
  overflow: hidden;
  touch-action: pan-y;
  transform: ${({ isOpen, bottomSheet, dragOffset }) =>
    isOpen ? (bottomSheet ? 'translateY(0)' : `translateY(${dragOffset}px)`) : 'translateY(100%)'};
  transition: ${({ dragOffset }) => (dragOffset === 0 ? 'transform 0.2s ease-in-out, top 0.2s ease-in-out' : 'none')};
`;

export const SheetContent = styled(Box)<{ hasHeader: boolean }>`
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  padding-left: ${({ theme }) => theme.spacing16}px;
  padding-right: ${({ theme }) => theme.spacing16}px;
  padding-top: ${({ hasHeader }) =>
    hasHeader ? `${MOBILE_MODAL_SHEET_HEADER_HEIGHT}px` : `${MOBILE_MODAL_SHEET_CONTENT_TOP_PADDING}px`};
`;

export const SheetTopGradient = styled(Box)<{ hasHeader: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${({ hasHeader }) =>
    hasHeader ? `${MOBILE_MODAL_SHEET_HEADER_HEIGHT}px` : `${MOBILE_MODAL_SHEET_CONTENT_TOP_PADDING}px`};
  z-index: 5;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.backgroundContent} 0%,
    ${({ theme }) => setAlpha(0.94, theme.backgroundContent)} 60%,
    ${({ theme }) => setAlpha(0.72, theme.backgroundContent)} 85%,
    ${({ theme }) => setAlpha(0, theme.backgroundContent)} 100%
  );
`;

export const MobileModalBackdropWrapper = styled(Box)<{ isOpen: boolean; transformContent?: boolean }>`
  flex: 1;
  opacity: ${({ isOpen }) => (isOpen ? 0.8 : 1)};
  transform: scale(1) translateY(0);
  transform-origin: top center;
  transition:
    transform 0.2s ease-in-out,
    border-radius 0.2s ease-in-out,
    top 0.2s ease-in-out;
  position: fixed;
  inset: 0;

  ${({ transformContent }) =>
    transformContent &&
    css`
      border-radius: ${MODAL_SHEET_BORDER_RADIUS}px;
      overflow: hidden;
      --scale: calc((100vw - 24px) / 100vw);
      transform: scale(var(--scale)) translateY(12px);
      pointer-events: none;
      top: env(safe-area-inset-top);
    `}
`;

export const HeaderContainer = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding-top: ${MOBILE_MODAL_SHEET_CONTENT_TOP_PADDING}px;
  z-index: 6;
  pointer-events: none;
`;

export const HeaderTitle = styled(Box)`
  position: relative;
  z-index: 1;
  text-align: center;
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  color: ${({ theme }) => theme.colorTextSubtle};
  padding: ${({ theme }) => `${theme.spacing6}px ${theme.spacing16}px`};
  margin-top: ${({ theme }) => theme.spacing8}px;
`;
